import { useMemo } from "react";

import { FieldArray, useField, useFormikContext } from "formik";

import {
  DateField,
  NumberField,
  SelectField,
  SwitchField,
  Switch3Field,
  TextField,
} from "components/form/fields";
import { Column, Row, VerticalTabs } from "components/layout";
import { FieldProps } from "types/form";
import { classes } from "utils/classes";
import { getFieldTestId } from "utils/formik";
import { isTabInvalid } from "utils/validation";

import { TEXTS } from "../constants";
import { FIELDS_PRESENTATION_CONFIG, FIELDS_PRESENTATION } from "../fields";
import { ProviderForm, TiVoPresentationConfigForm } from "../types";
import { getInitialPresentationConfig } from "../utils";
import { CountriesCompactField } from "./CountriesCompactField";
import { ListField } from "./ListField";
import styles from "./PresentationConfigs.scss";

export type PresentationConfigsProps = {
  name: string;
};

function getTabFields(name: string, index: number) {
  return Object.values(FIELDS_PRESENTATION_CONFIG).map(
    (field) => `${name}[${index}].${field.name}`
  );
}

export function getTabName(config: TiVoPresentationConfigForm) {
  const matchValue = config.matchByPlatformSupportedFeatures
    ? config.platformSupportedFeatures?.filter((v) => v)
    : config.countries;
  if (!matchValue) {
    return TEXTS.PRESENTATION.DEFAULT_CONFIG_LABEL;
  }
  if (matchValue.length === 0) {
    return TEXTS.PRESENTATION.NEW_CONFIG_LABEL;
  }
  return matchValue.join(", ");
}

function isDefaultTab(config: TiVoPresentationConfigForm) {
  return !config.countries && !config.platformSupportedFeatures;
}

function filterInheritedOption(
  values: FieldProps["values"],
  defaultTab: boolean
) {
  if (defaultTab) {
    return values?.filter(({ value }) => value !== "inherited");
  }
  return values;
}

const SWITCH_KEYS = [
  "ALWAYS_VISIBLE_IN_MY_APPS",
  "APP_MANIFEST_AVAILABLE",
  "ENABLED_BY_DEFAULT",
  "IN_MY_APPS_BY_DEFAULT",
  "PROMOTED",
  "SHOULD_SHOW_IN_LIST",
  "SHOULD_SHOW_IN_SEARCH_IN_APPS",
  "SUPPORTS_EPISODE_DEEPLINKS",
] as const;

const NUMBER_KEYS = [
  "SORT_ORDER",
  "MY_APPS_RANGE_GROUP",
  "BROWSE_PLAYBACK_SORT_ORDER",
  "POST_PUBLISH_DAYS_CONSIDERED_NEW",
] as const;

export function PresentationConfigs({ name }: PresentationConfigsProps) {
  const [, { value: configs }] = useField<TiVoPresentationConfigForm[]>(name);
  const { errors } = useFormikContext<ProviderForm>();
  const testId = getFieldTestId(FIELDS_PRESENTATION.CONFIGS.name);

  const tabNames = useMemo(
    () =>
      configs.map((config, index) => ({
        default: isDefaultTab(config),
        invalid: isTabInvalid(getTabFields(name, index), errors),
        name: getTabName(config),
        noRemove: isDefaultTab(config),
      })),
    [configs, errors, name]
  );

  const getFieldName = (
    tabIndex: number,
    fieldKey: keyof typeof FIELDS_PRESENTATION_CONFIG
  ) => `${name}.${tabIndex}.${FIELDS_PRESENTATION_CONFIG[fieldKey].name}`;

  return (
    <FieldArray name={name}>
      {({ push, remove }) => (
        <VerticalTabs
          tabNames={tabNames}
          dataTestId={`${testId}-tabs`}
          onAdd={() => push(getInitialPresentationConfig())}
          onRemove={(tabIndex: number) => remove(tabIndex)}
        >
          {(tabIndex: number) => {
            const config = configs[tabIndex];
            if (!config) {
              return null;
            }
            const defaultTab = isDefaultTab(config);
            const placeholder = defaultTab
              ? ""
              : TEXTS.PRESENTATION.INHERITED_PLACEHOLDER;
            return (
              <div>
                {!defaultTab && (
                  <Row>
                    <Column>
                      {!config.matchByPlatformSupportedFeatures && (
                        <CountriesCompactField
                          {...FIELDS_PRESENTATION_CONFIG.COUNTRIES}
                          name={getFieldName(tabIndex, "COUNTRIES")}
                        />
                      )}
                      {config.matchByPlatformSupportedFeatures && (
                        <ListField<string>
                          {...FIELDS_PRESENTATION_CONFIG.PLATFORM_SUPPORTED_FEATURES}
                          name={getFieldName(
                            tabIndex,
                            "PLATFORM_SUPPORTED_FEATURES"
                          )}
                          template=""
                        >
                          {(field) => (
                            <TextField
                              {...field}
                              look="simple"
                              placeholder={
                                TEXTS.PRESENTATION
                                  .PLATFORM_SUPPORTED_FEATURE_PLACEHOLDER
                              }
                            />
                          )}
                        </ListField>
                      )}
                    </Column>
                  </Row>
                )}
                <Row>
                  <Column>
                    {!defaultTab && (
                      <div
                        className={classes(
                          styles.switchList,
                          styles.noMarginTop
                        )}
                      >
                        <SwitchField
                          {...FIELDS_PRESENTATION_CONFIG.MATCH_BY_PLATFORM_SUPPORTED_FEATURES}
                          name={getFieldName(
                            tabIndex,
                            "MATCH_BY_PLATFORM_SUPPORTED_FEATURES"
                          )}
                        />
                      </div>
                    )}
                    <div className={styles.switchList}>
                      {SWITCH_KEYS.map((key) => (
                        <Switch3Field
                          key={key}
                          {...FIELDS_PRESENTATION_CONFIG[key]}
                          name={getFieldName(tabIndex, key)}
                          values={filterInheritedOption(
                            FIELDS_PRESENTATION_CONFIG[key].values,
                            defaultTab
                          )}
                        />
                      ))}
                    </div>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <TextField
                      {...FIELDS_PRESENTATION_CONFIG.NAME}
                      name={getFieldName(tabIndex, "NAME")}
                      placeholder={placeholder}
                    />
                    <SelectField
                      {...FIELDS_PRESENTATION_CONFIG.RECOMMENDATION_TYPE}
                      name={getFieldName(tabIndex, "RECOMMENDATION_TYPE")}
                      values={filterInheritedOption(
                        FIELDS_PRESENTATION_CONFIG.RECOMMENDATION_TYPE.values,
                        defaultTab
                      )}
                      placeholder={placeholder}
                    />
                    <ListField<string>
                      {...FIELDS_PRESENTATION_CONFIG.PLATFORM_SUPPORTED_APPS}
                      name={getFieldName(tabIndex, "PLATFORM_SUPPORTED_APPS")}
                      template=""
                    >
                      {(field) => (
                        <TextField
                          {...field}
                          look="simple"
                          placeholder={
                            TEXTS.PRESENTATION
                              .PLATFORM_SUPPORTED_APP_PLACEHOLDER
                          }
                        />
                      )}
                    </ListField>
                  </Column>
                  <Column>
                    {NUMBER_KEYS.map((key) => (
                      <NumberField
                        key={key}
                        {...FIELDS_PRESENTATION_CONFIG[key]}
                        name={getFieldName(tabIndex, key)}
                        placeholder={placeholder}
                      />
                    ))}
                    <DateField
                      {...FIELDS_PRESENTATION_CONFIG.PUBLISH_DATE}
                      name={getFieldName(tabIndex, "PUBLISH_DATE")}
                    />
                  </Column>
                </Row>
              </div>
            );
          }}
        </VerticalTabs>
      )}
    </FieldArray>
  );
}
